import { useState, useEffect } from 'react'
import { TenantGuard } from '@/components/TenantGuard'
import { useAuth } from '@/hooks/useApi'
import { useTenantContext } from '@/contexts/TenantContext'
import { apiClient } from '@/lib/api'
import { cognitoAuth } from '@/lib/cognito'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Badge } from '@/components/ui/badge'
import { SmsHistoryModal } from '@/components/ui/sms-history-modal'
import { MetricCard } from '@/components/ui/metric-card'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { RefreshCw, Phone, Clock, User, CheckCircle, XCircle, AlertCircle, ChevronDown, ChevronUp, BarChart3, PhoneCall, UserCheck } from 'lucide-react'
import { CountdownTimer } from '@/components/ui/countdown-timer'
import { LiveTimer } from '@/components/ui/live-timer'

interface QueueEntry {
  sk?: string
  uuid?: string
  entry_id?: string
  phone: string
  name?: string
  position: number
  status: string
  epoch?: number
  joined_at?: number
  called_at?: number
  arrived_at?: number
  started_at?: number
  completed_at?: number
  cancelled_at?: number
  left_at?: number
  strike_count?: number
  delay_until?: number
  party_size?: number
  meta?: any
}

interface Queue {
  location_id: string
  service_id: string
  waiting: number
  called: number
  being_serviced: number
  no_show: number
  archived: boolean
  allow_multi_called?: boolean
  party_size_enabled?: boolean
}

export function QueueManager() {
  const { user } = useAuth();
  const { selectedTenantId } = useTenantContext();
  const activeTenantId = selectedTenantId || user?.tenant_from_group;
  

  const [queues, setQueues] = useState<Queue[]>([])
  const [selectedQueue, setSelectedQueue] = useState<string>('')
  const [entries, setEntries] = useState<QueueEntry[]>([])
  const [loading, setLoading] = useState(false)
  const [autoRefresh, setAutoRefresh] = useState(false)
  const [showOverview, setShowOverview] = useState(false)
  const [smsModal, setSmsModal] = useState<{
    isOpen: boolean
    phone: string
    entryId: string
  }>({ isOpen: false, phone: '', entryId: '' })
  const [removeModal, setRemoveModal] = useState<{
    isOpen: boolean
    entryId: string
    entryName: string
  }>({ isOpen: false, entryId: '', entryName: '' })
  const [noShowFilter, setNoShowFilter] = useState<'requeuing' | 'recent' | 'all'>('requeuing')
  const [smsFailures] = useState<Map<string, string>>(new Map()) // entryId -> failure reason
  const [processingActions, setProcessingActions] = useState<Set<string>>(new Set()) // Track processing for button disable

  const getId = (entry: QueueEntry) => {
    return entry?.sk || entry?.uuid || entry?.entry_id || ''
  }

  const fetchQueues = async () => {
    if (!activeTenantId) return
    
    try {
      // Ensure token is set before API call
      const token = cognitoAuth.getIdToken()
      if (token) {
        apiClient.setToken(token)
      } else {
        console.error('No authentication token available')
        return
      }
      
      console.log('Fetching queues for tenant:', activeTenantId)
      const response = await apiClient.getQueuesSummary(activeTenantId)
      console.log('Queues response:', response)
      const activeQueues = (response.queues || []).filter((q: Queue) => !q.archived)
      setQueues(activeQueues)
      
      if (activeQueues.length > 0 && !selectedQueue) {
        setSelectedQueue(`${activeQueues[0].location_id}/${activeQueues[0].service_id}`)
      }
    } catch (err) {
      console.error('Failed to fetch queues:', err)
    }
  }

  const fetchQueueEntries = async () => {
    if (!activeTenantId || !selectedQueue) return
    
    const [locationId, serviceId] = selectedQueue.split('/')
    if (!locationId || !serviceId) return
    
    try {
      setLoading(true)
      
      // Ensure token is set before API call
      const token = cognitoAuth.getIdToken()
      if (token) {
        apiClient.setToken(token)
      } else {
        console.error('No authentication token available')
        return
      }
      
      const response = await apiClient.getQueueEntries(activeTenantId, locationId, serviceId)
      
      // Combine all entries preserving their actual status from backend
      const allEntries = [
        ...(response.waiting || []).map((entry: any) => ({ ...entry, status: entry.status || 'waiting' })),
        ...(response.called || []).map((entry: any) => ({ ...entry, status: entry.status || 'called' })),
        ...(response.being_serviced || []).map((entry: any) => ({ ...entry, status: entry.status || 'being_serviced' })),
        ...(response.no_show || []).map((entry: any) => ({ ...entry, status: entry.status || 'no_show' })),
        ...(response.delayed || []).map((entry: any) => ({ ...entry, status: entry.status || 'no_show_delayed' }))
      ]
      
      setEntries(allEntries)
    } catch (err) {
      console.error('Failed to fetch queue entries:', err)
    } finally {
      setLoading(false)
    }
  }

  const handleAction = async (entryId: string, action: string) => {
    if (!activeTenantId || !selectedQueue) return
    if (processingActions.has(entryId)) return // Prevent double-click
    
    // Disable button IMMEDIATELY to prevent race conditions
    setProcessingActions(prev => new Set(prev).add(entryId))
    
    const [locationId, serviceId] = selectedQueue.split('/')
    
    try {
      // Ensure token is set before API call
      const token = cognitoAuth.getIdToken()
      if (token) {
        apiClient.setToken(token)
      } else {
        console.error('No authentication token available')
        return
      }
      
      console.log(`Starting ${action} for entry ${entryId}`)
      
      let response: any
      
      // Handle different action types with proper API calls
      if (action === 'call') {
        // For first customer, use call_next, otherwise call_entry
        const waitingEntries = entries.filter(e => e.status === 'waiting')
        const isFirst = waitingEntries.length > 0 && getId(waitingEntries[0]) === entryId
        
        if (isFirst) {
          response = await apiClient.callNext(activeTenantId, locationId, serviceId)
        } else {
          response = await apiClient.updateQueueEntry(activeTenantId, locationId, serviceId, entryId, 'call_entry')
        }
      } else if (action === 'mark_arrived') {
        response = await apiClient.markArrived(activeTenantId, locationId, serviceId, entryId)
      } else if (action === 'mark_no_show') {
        response = await apiClient.markNoShow(activeTenantId, locationId, serviceId, entryId)
      } else if (action === 'close_entry') {
        response = await apiClient.updateQueueEntry(activeTenantId, locationId, serviceId, entryId, 'close')
      } else {
        response = await apiClient.updateQueueEntry(activeTenantId, locationId, serviceId, entryId, action)
      }
      
      console.log(`${action} completed successfully:`, response)
      
      // For call actions, let the finally block handle the refresh
      console.log(`${action} completed successfully for entry ${entryId}`)
      
      // Handle push_back_entry and readd_entry responses - update UI immediately with new entry
      if ((action === 'push_back_entry' || action === 'readd_entry') && response?.ok && response?.entry_id) {
        const smsStatus = response?.sms_sent ? 'SMS sent successfully' : (response?.sms_reason || 'SMS not sent')
        const actionName = action === 'push_back_entry' ? 'Push back' : 'No-show requeue'
        console.log(`${actionName} completed. New entry ID: ${response.entry_id}. ${smsStatus}`)
        
        // Find the original entry and update it with new ID and status
        setEntries(prevEntries => {
          const updatedEntries = prevEntries.map(entry => {
            if (getId(entry) === entryId) {
              // Update the entry with new ID and move to waiting status
              return {
                ...entry,
                sk: response.entry_id,
                entry_id: response.entry_id,
                status: 'waiting',
                // Reset timestamps since it's back to waiting
                called_at: undefined,
                arrived_at: undefined,
                started_at: undefined,
                no_show_at: undefined,
                delay_until: undefined,
                strike_count: entry.strike_count // Preserve strike count
              }
            }
            return entry
          })
          return updatedEntries
        })
        
        // Clear processing state for both old and new IDs
        setProcessingActions(prev => {
          const next = new Set(prev)
          next.delete(entryId) // Remove old ID
          next.delete(response.entry_id) // Remove new ID (in case it was somehow added)
          return next
        })
        return // Skip the refresh since we updated locally
      }
      
    } catch (err) {
      console.error(`Failed to ${action}:`, err)
      alert(`Failed to ${action}: ${err instanceof Error ? err.message : 'Unknown error'}`)
    } finally {
      // Clear API cache to force fresh data on next request
      apiClient.clearCache(`/admin/tenant/${activeTenantId}/queue`)
      
      // Always refresh and re-enable button for non-requeue actions
      await fetchQueueEntries()
      await fetchQueues()
      setProcessingActions(prev => {
        const next = new Set(prev)
        next.delete(entryId)
        return next
      })
    }
  }

  const handleRemoveWithReason = async (entryId: string, reason: string) => {
    if (!activeTenantId || !selectedQueue) return
    
    const [locationId, serviceId] = selectedQueue.split('/')
    setRemoveModal({ isOpen: false, entryId: '', entryName: '' })
    
    try {
      // Ensure token is set before API call
      const token = cognitoAuth.getIdToken()
      if (token) {
        apiClient.setToken(token)
      } else {
        console.error('No authentication token available')
        return
      }
      
      await apiClient.removeQueueEntry(activeTenantId, locationId, serviceId, entryId, reason)
      
      // Clear API cache to force fresh data
      apiClient.clearCache(`/admin/tenant/${activeTenantId}/queue`)
      
      // Refresh data after API call completes
      await fetchQueueEntries()
      await fetchQueues()
      
    } catch (err) {
      console.error(`Failed to remove with reason ${reason}:`, err)
      alert(`Failed to remove customer: ${err instanceof Error ? err.message : 'Unknown error'}`)
      
      // Refresh to get current state
      await fetchQueueEntries()
      await fetchQueues()
    }
  }

  useEffect(() => {
    if (activeTenantId) {
      setSelectedQueue('')
      setEntries([])
      fetchQueues()
    }
  }, [activeTenantId])

  useEffect(() => {
    fetchQueueEntries()
  }, [selectedQueue])
  
  // Listen for tenant-changed event from dropdown
  useEffect(() => {
    const handleTenantChanged = () => {
      setSelectedQueue('')
      setEntries([])
      fetchQueues()
    }
    
    window.addEventListener('tenant-changed', handleTenantChanged as EventListener)
    
    return () => {
      window.removeEventListener('tenant-changed', handleTenantChanged as EventListener)
    }
  }, [])
  


  useEffect(() => {
    if (!autoRefresh) return
    
    const interval = setInterval(() => {
      fetchQueueEntries()
      fetchQueues()
    }, 5000)
    
    return () => clearInterval(interval)
  }, [autoRefresh, selectedQueue])

  const formatTime = (epoch: number) => {
    return new Date(epoch * 1000).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
  }



  const waitingEntries = entries.filter(e => e.status === 'waiting')
  const calledEntries = entries.filter(e => e.status === 'called')
  const servingEntries = entries.filter(e => e.status === 'being_serviced')
  
  // Filter no-show entries based on selected filter
  const allNoShowEntries = entries.filter(e => e.status === 'no_show' || e.status === 'no_show_delayed')
  const noshowEntries = allNoShowEntries.filter(entry => {
    if (noShowFilter === 'requeuing') {
      return entry.status === 'no_show_delayed'
    } else if (noShowFilter === 'recent') {
      if (entry.status === 'no_show_delayed') return true
      // Show permanent no-shows from last 2 hours
      const twoHoursAgo = Math.floor(Date.now() / 1000) - (2 * 60 * 60)
      const noShowTime = (entry as any).no_show_at || (entry as any).cancelled_at || 0
      return noShowTime > twoHoursAgo
    }
    return true // 'all' filter
  })

  const selectedQueueData = queues.find(q => `${q.location_id}/${q.service_id}` === selectedQueue)

  const renderWaitingEntry = (entry: QueueEntry, index: number) => {
    const isFirst = index === 0
    const canCall = isFirst || selectedQueueData?.allow_multi_called
    const showPartySize = selectedQueueData?.party_size_enabled && entry.party_size && entry.party_size > 0
    const isProcessing = processingActions.has(getId(entry))
    
    return (
      <Card key={getId(entry)} className={`mb-4 ${!canCall ? 'opacity-60' : ''}`}>
        <CardContent className="p-4">
          <div className="flex justify-between items-start mb-3">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <User className="h-4 w-4 text-gray-500" />
                <span className="font-semibold">{entry.name || 'Anonymous'}</span>
                <Badge className="bg-blue-600 text-white">#{index + 1}</Badge>
                {showPartySize && (
                  <div className="flex items-center gap-1 px-3 py-1 rounded-full bg-gradient-to-r from-purple-600 to-indigo-600 text-white shadow-md">
                    <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M9 6a3 3 0 11-6 0 3 3 0 016 0zM17 6a3 3 0 11-6 0 3 3 0 016 0zM12.93 17c.046-.327.07-.66.07-1a6.97 6.97 0 00-1.5-4.33A5 5 0 0119 16v1h-6.07zM6 11a5 5 0 015 5v1H1v-1a5 5 0 015-5z" />
                    </svg>
                    <span className="text-xl font-bold">{entry.party_size}</span>
                  </div>
                )}
                {entry.strike_count && entry.strike_count > 0 && (
                  <Badge className="bg-red-600 text-white">⚠️ {entry.strike_count} strikes</Badge>
                )}
              </div>
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <Phone className="h-3 w-3" />
                <a href={`tel:${entry.phone}`} className="hover:text-blue-600">
                  {entry.phone}
                </a>
              </div>
              <div className="flex items-center gap-2 text-sm text-gray-500 mt-1">
                <Clock className="h-3 w-3" />
                <span>Joined: {formatTime(entry.epoch || 0)}</span>
                <span>•</span>
                <LiveTimer startTime={entry.epoch || 0} className="text-blue-600 font-medium" />
              </div>
              {entry.meta?.answers && Object.keys(entry.meta.answers).length > 0 && (
                <div className="mt-2 p-2 bg-gray-50 rounded text-xs">
                  <div className="font-medium text-gray-700 mb-1">Answers:</div>
                  {Object.entries(entry.meta.answers).map(([key, value]) => (
                    <div key={key} className="text-gray-600">
                      <span className="font-medium">{key}:</span> {String(value)}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <Badge className="bg-blue-100 text-blue-800">Waiting</Badge>
          </div>
          
          <div className="flex gap-2 flex-wrap">
            <Button
              size="sm"
              onClick={() => handleAction(getId(entry), 'call')}
              className="bg-yellow-600 hover:bg-yellow-700"
              disabled={!canCall || isProcessing}
            >
              📞 Call Customer
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => handleAction(getId(entry), 'push_back_entry')}
              disabled={!canCall || isProcessing}
            >
              ↩️ Push Back
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setRemoveModal({ 
                isOpen: true, 
                entryId: getId(entry), 
                entryName: entry.name || 'Anonymous' 
              })}
              disabled={!canCall || isProcessing}
            >
              ❌ Remove
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                const entryId = getId(entry)
                console.log('SMS History clicked:', {
                  phone: entry.phone,
                  entryId,
                  selectedQueue,
                  locationId: selectedQueue.split('/')[0],
                  serviceId: selectedQueue.split('/')[1],
                  activeTenantId
                })
                setSmsModal({ isOpen: true, phone: entry.phone, entryId })
              }}
            >
              📋 History
            </Button>
          </div>
        </CardContent>
      </Card>
    )
  }

  const renderEntry = (entry: QueueEntry, showActions = true) => {
    const showPartySize = selectedQueueData?.party_size_enabled && entry.party_size && entry.party_size > 0
    const isProcessing = processingActions.has(getId(entry))
    
    return (
      <Card key={getId(entry)} className="mb-4">
        <CardContent className="p-4">
          <div className="flex justify-between items-start mb-3">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <User className="h-4 w-4 text-gray-500" />
                <span className="font-semibold">{entry.name || 'Anonymous'}</span>
                <Badge variant="outline">#{entry.position || 0}</Badge>
                {showPartySize && (
                  <div className="flex items-center gap-1 px-3 py-1 rounded-full bg-gradient-to-r from-purple-600 to-indigo-600 text-white shadow-md">
                    <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M9 6a3 3 0 11-6 0 3 3 0 016 0zM17 6a3 3 0 11-6 0 3 3 0 016 0zM12.93 17c.046-.327.07-.66.07-1a6.97 6.97 0 00-1.5-4.33A5 5 0 0119 16v1h-6.07zM6 11a5 5 0 015 5v1H1v-1a5 5 0 015-5z" />
                    </svg>
                    <span className="text-xl font-bold">{entry.party_size}</span>
                  </div>
                )}
                {entry.strike_count && entry.strike_count > 0 && (
                  <Badge className="bg-red-600 text-white">⚠️ {entry.strike_count} strikes</Badge>
                )}
              </div>
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <Phone className="h-3 w-3" />
              <a href={`tel:${entry.phone}`} className="hover:text-blue-600">
                {entry.phone}
              </a>
            </div>
            <div className="flex items-center gap-2 text-sm text-gray-500 mt-1">
              <Clock className="h-3 w-3" />
              <span>Joined: {formatTime(entry.epoch || 0)}</span>
              <span>•</span>
              {entry.status === 'waiting' && (
                <LiveTimer startTime={entry.epoch || 0} className="text-blue-600 font-medium" showIcon={false} />
              )}
              {entry.status === 'called' && entry.called_at && (
                <>
                  <span>Called: </span>
                  <LiveTimer startTime={typeof entry.called_at === 'string' ? parseInt(entry.called_at) : entry.called_at} className="text-yellow-600 font-medium" showIcon={false} />
                </>
              )}
              {entry.status === 'being_serviced' && (entry.arrived_at || entry.started_at) && (
                <>
                  <span>Serving: </span>
                  <LiveTimer startTime={entry.arrived_at || (entry as any).started_at} className="text-green-600 font-medium" showIcon={false} />
                </>
              )}
              {(entry as any).delay_until && (entry as any).delay_until > Math.floor(Date.now() / 1000) && (
                <>
                  <span>•</span>
                  <CountdownTimer 
                    targetTime={(entry as any).delay_until} 
                    onComplete={() => {
                      // Clear entries immediately to show requeuing state
                      setEntries([])
                      setQueues([])
                      // Refresh after EventBridge execution delay
                      fetchQueueEntries()
                      fetchQueues()
                    }}
                  />
                </>
              )}
            </div>
            {entry.meta?.answers && Object.keys(entry.meta.answers).length > 0 && (
              <div className="mt-2 p-2 bg-gray-50 rounded text-xs">
                <div className="font-medium text-gray-700 mb-1">Answers:</div>
                {Object.entries(entry.meta.answers).map(([key, value]) => (
                  <div key={key} className="text-gray-600">
                    <span className="font-medium">{key}:</span> {String(value)}
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className="flex items-center gap-2">
            {entry.status === 'waiting' && (
              <>
                <Badge className="bg-blue-100 text-blue-800">Waiting</Badge>
                {smsFailures.has(getId(entry)) && (
                  <Badge 
                    className="bg-red-600 text-white animate-pulse cursor-help" 
                    title={`SMS Failed: ${smsFailures.get(getId(entry))}`}
                  >
                    🚨 SMS FAILED
                  </Badge>
                )}
              </>
            )}
            {entry.status === 'called' && (
              <>
                <Badge className="bg-yellow-100 text-yellow-800">Called</Badge>
                {smsFailures.has(getId(entry)) && (
                  <Badge 
                    className="bg-red-600 text-white animate-pulse cursor-help" 
                    title={`SMS Failed: ${smsFailures.get(getId(entry))}`}
                  >
                    🚨 SMS FAILED - NOTIFY MANUALLY
                  </Badge>
                )}
              </>
            )}
            {entry.status === 'being_serviced' && (
              <>
                <Badge className="bg-green-100 text-green-800">Serving</Badge>
                {smsFailures.has(getId(entry)) && (
                  <Badge 
                    className="bg-red-600 text-white animate-pulse cursor-help" 
                    title={`SMS Failed: ${smsFailures.get(getId(entry))}`}
                  >
                    🚨 SMS FAILED
                  </Badge>
                )}
              </>
            )}
            {(entry.status === 'no_show' || entry.status === 'no_show_delayed') && (
              <>
                <Badge className="bg-red-100 text-red-800">
                  {entry.status === 'no_show_delayed' ? 'No Show (Requeuing)' : 'No Show'}
                </Badge>
                {smsFailures.has(getId(entry)) && (
                  <Badge 
                    className="bg-red-600 text-white animate-pulse cursor-help" 
                    title={`SMS Failed: ${smsFailures.get(getId(entry))}`}
                  >
                    🚨 SMS FAILED
                  </Badge>
                )}
              </>
            )}
          </div>
        </div>
        
        {showActions && (
          <div className="flex gap-2 flex-wrap">
            {entry.status === 'waiting' && (
              <>
                <Button
                  size="sm"
                  onClick={() => handleAction(getId(entry), 'call')}
                  className="bg-yellow-600 hover:bg-yellow-700"
                  disabled={isProcessing}
                >
                  📞 Call
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleAction(getId(entry), 'mark_no_show')}
                  disabled={isProcessing}
                >
                  ❌ No Show
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    if (confirm('Force remove this customer from the queue?')) {
                      handleAction(getId(entry), 'cancel_entry')
                    }
                  }}
                  disabled={isProcessing}
                >
                  🚫 Force Remove
                </Button>
              </>
            )}
            {entry.status === 'called' && (
              <>
                <Button
                  size="sm"
                  onClick={() => handleAction(getId(entry), 'mark_arrived')}
                  className="bg-green-600 hover:bg-green-700"
                  disabled={isProcessing}
                >
                  {isProcessing ? '⏳' : '▶️'} Start Service
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleAction(getId(entry), 'mark_no_show')}
                  disabled={isProcessing}
                >
                  ❌ No Show
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleAction(getId(entry), 'push_back_entry')}
                  disabled={isProcessing}
                >
                  ↩️ Requeue
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setRemoveModal({ 
                    isOpen: true, 
                    entryId: getId(entry), 
                    entryName: entry.name || 'Anonymous' 
                  })}
                  disabled={isProcessing}
                >
                  ❌ Remove
                </Button>
              </>
            )}
            {entry.status === 'being_serviced' && (
              <>
                <Button
                  size="sm"
                  onClick={() => handleAction(getId(entry), 'close_entry')}
                  className="bg-green-600 hover:bg-green-700"
                  disabled={isProcessing}
                >
                  {isProcessing ? '⏳' : '✅'} Complete
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleAction(getId(entry), 'push_back_entry')}
                  disabled={isProcessing}
                >
                  ↩️ Requeue
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setRemoveModal({ 
                    isOpen: true, 
                    entryId: getId(entry), 
                    entryName: entry.name || 'Anonymous' 
                  })}
                  disabled={isProcessing}
                >
                  🚫 Force Remove
                </Button>
              </>
            )}
            {(entry.status === 'no_show' || entry.status === 'no_show_delayed') && (
              <>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleAction(getId(entry), 'readd_entry')}
                  className="bg-blue-50 hover:bg-blue-100"
                  disabled={isProcessing}
                >
                  {isProcessing ? '⏳' : '↩️'} Requeue Now
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    if (confirm('Remove this customer from the queue permanently?')) {
                      handleAction(getId(entry), 'cancel_entry')
                    }
                  }}
                  className="text-red-600 hover:text-red-700"
                  disabled={isProcessing}
                >
                  🗑️ Remove
                </Button>
              </>
            )}
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                const entryId = getId(entry)
                console.log('SMS History clicked:', {
                  phone: entry.phone,
                  entryId,
                  selectedQueue,
                  locationId: selectedQueue.split('/')[0],
                  serviceId: selectedQueue.split('/')[1],
                  activeTenantId
                })
                setSmsModal({ isOpen: true, phone: entry.phone, entryId })
              }}
              disabled={isProcessing}
            >
              📋 History
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
    )
  }

  return (
    <TenantGuard>
      <div key={activeTenantId} className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold">Queue Manager</h1>
          <p className="text-gray-600">
            Manage queue operations
          </p>
        </div>

        {/* Queue Overview Dashboard */}
        <Card>
          <CardHeader>
            <div className="flex justify-between items-center">
              <CardTitle className="flex items-center gap-2">
                <BarChart3 className="h-5 w-5" />
                All Queues Overview
              </CardTitle>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowOverview(!showOverview)}
              >
                {showOverview ? (
                  <><ChevronUp className="h-4 w-4 mr-1" /> Hide</>
                ) : (
                  <><ChevronDown className="h-4 w-4 mr-1" /> Show</>
                )}
              </Button>
            </div>
          </CardHeader>
          {showOverview && (
            <CardContent>
              {/* Individual Queue Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {queues.map(queue => {
                  const queueKey = `${queue.location_id}/${queue.service_id}`
                  const isSelected = queueKey === selectedQueue
                  
                  // Calculate actual no-shows for this queue from entries if it's the selected queue
                  let actualNoShows = queue.no_show || 0
                  if (isSelected) {
                    actualNoShows = entries.filter(e => e.status === 'no_show' || e.status === 'no_show_delayed').length
                  }
                  
                  return (
                    <Card 
                      key={queueKey}
                      className={`cursor-pointer transition-all hover:shadow-md ${
                        isSelected ? 'ring-2 ring-blue-500 bg-blue-50' : 'hover:bg-gray-50'
                      }`}
                      onClick={() => setSelectedQueue(queueKey)}
                    >
                      <CardContent className="p-4">
                        <div className="font-semibold text-sm mb-3 truncate">
                          {queue.location_id} / {queue.service_id}
                        </div>
                        <div className="grid grid-cols-2 gap-2 text-xs">
                          <div className="text-center">
                            <div className="font-bold text-blue-600">{queue.waiting || 0}</div>
                            <div className="text-gray-500">Waiting</div>
                          </div>
                          <div className="text-center">
                            <div className="font-bold text-yellow-600">{queue.called || 0}</div>
                            <div className="text-gray-500">Called</div>
                          </div>
                          <div className="text-center">
                            <div className="font-bold text-green-600">{queue.being_serviced || 0}</div>
                            <div className="text-gray-500">Serving</div>
                          </div>
                          <div className="text-center">
                            <div className="font-bold text-red-600">{actualNoShows}</div>
                            <div className="text-gray-500">No Shows</div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  )
                })}
              </div>
            </CardContent>
          )}
        </Card>

        {/* Selected Queue Stats */}
        {selectedQueue && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <MetricCard
              title="Waiting"
              value={waitingEntries.length}
              icon={<Clock className="h-4 w-4 text-blue-600" />}
              className="border-l-4 border-l-blue-600"
            />
            <MetricCard
              title="Called"
              value={calledEntries.length}
              icon={<PhoneCall className="h-4 w-4 text-yellow-600" />}
              className="border-l-4 border-l-yellow-600"
            />
            <MetricCard
              title="Being Served"
              value={servingEntries.length}
              icon={<UserCheck className="h-4 w-4 text-green-600" />}
              className="border-l-4 border-l-green-600"
            />
            <MetricCard
              title="No Shows"
              value={noshowEntries.length}
              icon={<XCircle className="h-4 w-4 text-red-600" />}
              className="border-l-4 border-l-red-600"
            />
          </div>
        )}

        {/* Queue Selector */}
        <Card>
          <CardHeader>
            <CardTitle>Select Queue</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex gap-4 items-center">
              <Select value={selectedQueue} onValueChange={setSelectedQueue}>
                <SelectTrigger className="flex-1">
                  <SelectValue placeholder="Select a queue to manage" />
                </SelectTrigger>
                <SelectContent>
                  {queues.map(queue => (
                    <SelectItem 
                      key={`${queue.location_id}/${queue.service_id}`}
                      value={`${queue.location_id}/${queue.service_id}`}
                    >
                      {queue.location_id} / {queue.service_id}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                variant="outline"
                onClick={() => {
                  // Clear API cache to force fresh data
                  apiClient.clearCache()
                  setEntries([])
                  setQueues([])
                  setTimeout(() => {
                    fetchQueueEntries()
                    fetchQueues()
                  }, 100)
                }}
                disabled={loading}
              >
                <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
              <Button
                variant={autoRefresh ? "default" : "outline"}
                onClick={() => setAutoRefresh(!autoRefresh)}
              >
                {autoRefresh ? '⏸️ Stop Auto' : '▶️ Auto Refresh'}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Being Served */}
        {servingEntries.length > 0 && (
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <CardTitle className="flex items-center gap-2">
                  <CheckCircle className="h-5 w-5 text-green-600" />
                  Currently Being Served ({servingEntries.length})
                </CardTitle>
                {servingEntries.length > 1 && (
                  <Button
                    onClick={() => {
                      if (confirm('Complete all customers being served?')) {
                        servingEntries.forEach(entry => handleAction(getId(entry), 'close_entry'))
                      }
                    }}
                    className="bg-green-600 hover:bg-green-700"
                  >
                    ✅ Complete All
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {servingEntries.map(entry => renderEntry(entry))}
            </CardContent>
          </Card>
        )}

        {/* Called Customers */}
        {calledEntries.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <AlertCircle className="h-5 w-5 text-yellow-600" />
                Called Customers ({calledEntries.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              {calledEntries.map(entry => renderEntry(entry))}
            </CardContent>
          </Card>
        )}

        {/* Waiting Queue */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="h-5 w-5 text-blue-600" />
              Waiting Queue ({waitingEntries.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {waitingEntries.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                No customers waiting
              </div>
            ) : (
              waitingEntries.map((entry, index) => renderWaitingEntry(entry, index))
            )}
          </CardContent>
        </Card>

        {/* No Shows */}
        <Card>
          <CardHeader>
            <div className="flex justify-between items-center">
              <CardTitle className="flex items-center gap-2">
                <XCircle className="h-5 w-5 text-red-600" />
                No Shows ({noshowEntries.length})
              </CardTitle>
              <Select value={noShowFilter} onValueChange={(value: 'requeuing' | 'recent' | 'all') => setNoShowFilter(value)}>
                <SelectTrigger className="w-48">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="requeuing">Requeuing Only</SelectItem>
                  <SelectItem value="recent">Recent (2 hours)</SelectItem>
                  <SelectItem value="all">All No Shows</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardHeader>
          <CardContent>
            {noshowEntries.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                {noShowFilter === 'requeuing' ? 'No customers requeuing' : 
                 noShowFilter === 'recent' ? 'No recent no-shows' : 
                 'No no-shows'}
              </div>
            ) : (
              noshowEntries.map(entry => renderEntry(entry))
            )}
          </CardContent>
        </Card>

        {/* SMS History Modal */}
        <SmsHistoryModal
          isOpen={smsModal.isOpen}
          onClose={() => setSmsModal({ isOpen: false, phone: '', entryId: '' })}
          phone={smsModal.phone}
          tenantId={activeTenantId || ''}
          locationId={selectedQueue.split('/')[0] || ''}
          serviceId={selectedQueue.split('/')[1] || ''}
          entryId={smsModal.entryId}
        />

        {/* Remove Reason Modal */}
        <Dialog open={removeModal.isOpen} onOpenChange={(open) => 
          setRemoveModal({ isOpen: open, entryId: '', entryName: '' })
        }>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Remove Customer: {removeModal.entryName}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <p className="text-sm text-gray-600">
                Please select the reason for removing this customer from the queue:
              </p>
              <div className="space-y-2">
                <Button
                  className="w-full justify-start bg-red-50 hover:bg-red-100 text-red-700 border-red-200"
                  variant="outline"
                  onClick={() => handleRemoveWithReason(removeModal.entryId, 'no_show')}
                >
                  ❌ No Show - Customer didn't arrive when called
                </Button>
                <Button
                  className="w-full justify-start bg-blue-50 hover:bg-blue-100 text-blue-700 border-blue-200"
                  variant="outline"
                  onClick={() => handleRemoveWithReason(removeModal.entryId, 'left')}
                >
                  🚪 Customer Left - Customer asked to leave or left voluntarily
                </Button>
                <Button
                  className="w-full justify-start bg-gray-50 hover:bg-gray-100 text-gray-700 border-gray-200"
                  variant="outline"
                  onClick={() => handleRemoveWithReason(removeModal.entryId, 'cancelled')}
                >
                  🚫 Staff Cancelled - Removed by staff for other reasons
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </TenantGuard>
  )
}